"use client";
import React, { createContext, useContext, useState, ReactNode } from "react";

type ModalComponent = React.ComponentType<any>;

interface ModalState {
  component: ModalComponent;
  props: Record<string, any>;
}

interface ModalContextType {
  openModal: (component: ModalComponent, props?: Record<string, any>) => void;
  closeModal: () => void;
  closeAllModals: () => void;
  modalStack: ModalState[];
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [modalStack, setModalStack] = useState<ModalState[]>([]);

  const openModal = (
    component: ModalComponent,
    props: Record<string, any> = {},
  ) => {
    setModalStack((prev) => [...prev, { component, props }]);
  };

  const closeModal = () => {
    setModalStack((prev) => prev.slice(0, prev.length - 1));
  };
  const closeAllModals = () => {
    setModalStack([]);
  };

  const currentModal = modalStack[modalStack.length - 1];

  return (
    <ModalContext.Provider
      value={{ openModal, closeModal, closeAllModals, modalStack }}
    >
      {children}
      {currentModal && <currentModal.component {...currentModal.props} />}
    </ModalContext.Provider>
  );
};

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) throw new Error("useModal must be used within a ModalProvider");
  return context;
};
